import { uniq } from "lodash";
import { applySnapshot, getSnapshot, types } from "mobx-state-tree";
import { IdEntity } from "modules/common/models/entity";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Queryable } from "modules/common/models/queryable";
import { Transport } from "modules/common/models/transport";
import { apiUrls } from "modules/common/services/communication/urls";
import { texts } from "modules/common/texts";
import { Constants } from "modules/root/models/constants";
import { buildAccessModules } from "./access-tree";

export const RoleAccessRecord = types
  .model({
    name: types.string,
    value: types.boolean,
    overwritten: types.boolean,
    /** что даёт должность без личных исключений */
    roleValue: types.optional(types.boolean, false),
    /** откуда право, если его даёт не должность: «Руководитель подразделения «…»» */
    reason: types.maybeNull(types.string),
  })
  .named("RoleAccessRecord");

export const RoleAccess = types
  .compose(
    Transport,
    Notificator,
    IdEntity,
    types.model({
      name: types.string,
      access: types.array(RoleAccessRecord),
      department: types.string,
    })
  )
  .views((self) => ({
    get label() {
      return self.name;
    },

    get sublabel() {
      return self.department;
    },

    get functionalities() {
      return self.access.filter((a) => a.value).map((a) => a.name);
    },
  }))
  .actions((self) => ({
    setWholeAccess: flow(function* (access: string[]) {
      try {
        const data: any = yield self.transport.post<any>(apiUrls.employeePositions.wholeAccess(self.id), {
          access,
        });

        applySnapshot(self, data);

        self.notify.success(texts.messages.saved);
        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      }
    }),

    setFunctionalityAccess: flow(function* (functionality: string, state: boolean) {
      try {
        const data: any = yield self.transport.post<any>(apiUrls.employeePositions.singleAccess(self.id), {
          functionality,
          state,
        });

        applySnapshot(self, data);

        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      }
    }),
  }))
  .named("RoleAccess");

export const AccessStore = types
  .compose(
    Transport,
    Notificator,
    Queryable,
    types.model({
      roles: types.array(RoleAccess),
      authorization: types.map(types.string),
    })
  )
  .views((self) => ({
    get filteredRoles() {
      const data = self.roles.map((role) => ({
        ...getSnapshot(role),
        label: role.label,
        sublabel: role.sublabel,
      }));

      return self.pureQuery
        ? data.filter((role) => {
            const text = `${role.name} ${role.department}`.toLowerCase();
            return text.indexOf(self.pureQuery) >= 0;
          })
        : data;
    },
  }))
  .views((self) => ({
    get departments() {
      return uniq(self.filteredRoles.map((r) => r.department));
    },
  }))
  .actions((self) => ({
    load: flow(function* () {
      applySnapshot(self.authorization, getSnapshot(Constants.authorization));

      try {
        const data = yield self.transport.get<RoleAccessSnapshotType[]>(apiUrls.employee.access);

        applySnapshot(self.roles, data);

        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      }
    }),
  }))
  .views((self) => ({
    get accessModules() {
      return buildAccessModules(Array.from(self.authorization.entries()));
    },
  }))
  .named("AccessStore");

export type RoleAccessRecordSnapshotType = typeof RoleAccessRecord.SnapshotType;
export type RoleAccessSnapshotType = typeof RoleAccess.SnapshotType;
export type RoleAccessType = typeof RoleAccess.Type;
export type AccessStoreSnapshotType = typeof AccessStore.SnapshotType;
export type AccessStoreType = typeof AccessStore.Type;

export const initialState = (): AccessStoreSnapshotType => ({
  roles: [],
  authorization: {},
  pureQuery: "",
  query: "",
});

export interface RoleData {
  id: string;
  label: string;
  sublabel: string;
  access: RoleAccessRecordSnapshotType[];
}
